// The "sky" theme: a third color mode that drifts through the day in
// lockstep with Dame's hourly Bluesky avatar (and the favicon — see
// api/favicon.js and og/time.js). Instead of two fixed palettes, every
// Eastern hour gets its own palette derived from the actual sky gradient
// in that hour's avatar frame.
//
// SKY_BANDS holds, per hour, the average color of the TOP and BOTTOM
// quarter of images/sky-avatars/<key>.jpg (sampled with sharp; the middle
// band is skipped because the "dame" wordmark pollutes it). The top band
// is the deep sky, the bottom band is the horizon — at dawn/dusk that's
// where the warm glow lives, so accents derived from it track the sunrise
// and sunset just like the art does.
//
// From those two anchors, paletteForHour() derives the full set of theme
// tokens (page, surfaces, ink ramp, rules, accents, scrims) with the same
// relative steps the static light/dark themes use in styles/theme.css.
// Hue and saturation stay true to the frame, but the page's LIGHTNESS is
// driven by SUN_CURVE — a hand-eased solar arc — so the app background
// physically tracks the sun: near-flat dark through the night, a steep
// climb across sunrise, a gentle crest at solar noon, and a steep fall
// at sunset. Hours whose curve value sits above the midpoint get dark
// ink on a light sky page; the rest get light ink on a deep-sky page —
// mirroring the avatar's own wordmark flip.
//
// applySkyTheme(hour) writes the palette as --sky-* custom properties on
// <html>; styles/theme.css maps the real tokens onto those vars inside
// [data-theme='sky'].

import { easternHour, avatarKeys, secondsUntilNextHour } from '../../og/time.js';

export { easternHour, secondsUntilNextHour };

const KEYS = avatarKeys();

/** The clock label ("12am" … "11pm") for an hour 0–23. */
export function skyHourKey(hour) {
  return KEYS[((hour % 24) + 24) % 24];
}

/** The hour as the chrome shows it: "1 PM", "12 AM". The key stays
 *  lowercase, since it names the avatar's files. */
export function skyHourLabel(hour) {
  return skyHourKey(hour).replace(/(am|pm)$/, (m) => ` ${m.toUpperCase()}`);
}

// Sampled top/bottom band averages per hour (0 = 12am … 23 = 11pm).
// Exported for the hour sheet, whose ribbon is drawn from the avatar's own
// sky (deep sky at the top, horizon at the bottom), so noon reads as cyan
// rather than as a white cell on a white sheet.
export const SKY_BANDS = [
  { top: '#111111', bottom: '#101111' }, // 12am
  { top: '#121314', bottom: '#161616' }, // 1am
  { top: '#161b1e', bottom: '#171818' }, // 2am
  { top: '#0f1213', bottom: '#212121' }, // 3am
  { top: '#090808', bottom: '#2b2215' }, // 4am
  { top: '#07091b', bottom: '#43290c' }, // 5am
  { top: '#181e3c', bottom: '#5f3c06' }, // 6am
  { top: '#3660df', bottom: '#cc9543' }, // 7am
  { top: '#3369ff', bottom: '#c5ac94' }, // 8am
  { top: '#658eff', bottom: '#bccbfc' }, // 9am
  { top: '#96b5ff', bottom: '#87cffa' }, // 10am
  { top: '#8aebf0', bottom: '#afdff6' }, // 11am
  { top: '#2ce9f3', bottom: '#a0dff7' }, // 12pm
  { top: '#1dd7fc', bottom: '#82eff4' }, // 1pm
  { top: '#00c2ff', bottom: '#5be9f7' }, // 2pm
  { top: '#0ad1fd', bottom: '#84cffd' }, // 3pm
  { top: '#0dc7ff', bottom: '#82b1ff' }, // 4pm
  { top: '#0088ff', bottom: '#8082fb' }, // 5pm
  { top: '#1055ff', bottom: '#b36e8f' }, // 6pm
  { top: '#191793', bottom: '#66225c' }, // 7pm
  { top: '#101256', bottom: '#382354' }, // 8pm
  { top: '#030611', bottom: '#2c1a34' }, // 9pm
  { top: '#010105', bottom: '#100b18' }, // 10pm
  { top: '#0a0b10', bottom: '#0a0b0c' }, // 11pm
];

// The page-lightness envelope, one value per hour — a hand-eased solar
// arc rather than a formula, because the shape matters more than the
// math. Calibrated to Eastern-timezone daylight (the hour itself already
// resolves in America/New_York): the night core (10pm–4am) is near-flat
// dark, dawn stirs at 5–6am, sunrise climbs through 7–8am, the daylight
// hours hold bright — cresting at 1pm, solar noon in EDT — and stay
// bright through 5pm. The light doesn't start leaving until 6pm, then
// steps down through sunset (7pm) and twilight (8pm) into night. Both
// twilight ramps are spread over three hours — an S-curve, not a cliff —
// so no single increment slams from day to night. Bulk of day and night:
// small increments. Sunrise/sunset shoulders: the biggest steps.
//
// An hour is "daylight" (dark ink on a light page) when its value is
// ≥ 0.5; the flip points (7am, 7pm) are where each S-curve crosses the
// middle.
const SUN_CURVE = [
  0.095, // 12am
  0.09,  // 1am — deepest night
  0.09,  // 2am
  0.095, // 3am
  0.10,  // 4am
  0.13,  // 5am — first light on the horizon
  0.26,  // 6am — dawn glow building
  0.60,  // 7am — sunrise crosses into day
  0.74,  // 8am — morning settles in
  0.81,  // 9am
  0.85,  // 10am
  0.86,  // 11am
  0.875, // 12pm
  0.88,  // 1pm — solar noon
  0.875, // 2pm
  0.87,  // 3pm
  0.86,  // 4pm
  0.83,  // 5pm — still full daylight
  0.70,  // 6pm — the light starts to leave
  0.38,  // 7pm — sunset crosses into night
  0.22,  // 8pm — twilight fading
  0.13,  // 9pm
  0.10,  // 10pm
  0.09,  // 11pm
];

/* ---------- small color kit (hex ⇄ rgb ⇄ hsl) ---------- */

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex([r, g, b]) {
  return (
    '#' + [r, g, b].map((v) => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, '0')).join('')
  );
}

function rgbToHsl([r, g, b]) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  switch (max) {
    case r: h = (g - b) / d + (g < b ? 6 : 0); break;
    case g: h = (b - r) / d + 2; break;
    default: h = (r - g) / d + 4;
  }
  return [h * 60, s, l];
}

function hslToRgb([h, s, l]) {
  h = ((h % 360) + 360) % 360 / 360;
  s = clamp01(s); l = clamp01(l);
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [
    Math.round(hue(h + 1 / 3) * 255),
    Math.round(hue(h) * 255),
    Math.round(hue(h - 1 / 3) * 255),
  ];
}

const hsl = (h, s, l) => rgbToHex(hslToRgb([h, s, l]));
// CSS's `color-mix(in srgb, a <weight>, b)`.
const mixHex = (a, b, weight) => {
  const [ra, ga, ba] = hexToRgb(a);
  const [rb, gb, bb] = hexToRgb(b);
  return rgbToHex([
    ra * weight + rb * (1 - weight),
    ga * weight + gb * (1 - weight),
    ba * weight + bb * (1 - weight),
  ]);
};
const rgba = ([r, g, b], a) => `rgba(${r}, ${g}, ${b}, ${a})`;

// WCAG 2 relative luminance and contrast ratio, on hex colors.
function luminance(hex) {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colors (1–21). */
export function contrastRatio(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/* ---------- palette derivation ---------- */

// The brighter true-horizon samples for the two shoulder hours, picked off
// the actual avatar frames (the averaged SKY_BANDS bottom is muddier than
// the glow at the very horizon). Used as the default "pop" a tuning
// override warms its borders/text/glow from. Other hours default to their
// own SKY_BANDS horizon band.
const SAMPLED_POP = { 7: '#efa82b', 18: '#ce7175' };

/** The default horizon "pop" color a tuning override starts from, per hour. */
export function defaultPopForHour(hour) {
  const h = ((hour % 24) + 24) % 24;
  return SAMPLED_POP[h] || SKY_BANDS[h].bottom;
}

/**
 * Build the untuned --sky-* var set for one hour, optionally with the page
 * color replaced (absolute background override) and the chrome-surface
 * offsets scaled (surfaceSep). With `pageOverride` null and `surfaceSep`
 * 1 this reproduces the historical paletteForHour output exactly. Also
 * returns a `ctx` with the raw HSL of every tunable token so an override
 * layer can re-derive them.
 */
function buildBaseVars(hour, { pageOverride = null, surfaceSep = 1 } = {}) {
  const h24 = ((hour % 24) + 24) % 24;
  const { top, bottom } = SKY_BANDS[h24];
  const topRgb = hexToRgb(top);
  const botRgb = hexToRgb(bottom);
  const base = topRgb.map((v, i) => (v + botRgb[i]) / 2);
  const [bh, bs] = rgbToHsl(base);
  const [th, ts] = rgbToHsl(topRgb);
  const [hh, hs] = rgbToHsl(botRgb); // horizon band
  const sun = SUN_CURVE[h24];
  const day = sun >= 0.5;
  const sep = typeof surfaceSep === 'number' ? surfaceSep : 1;
  // An absolute page-color override supplies its own hue/sat/lightness,
  // and the whole neutral ramp (page, surfaces, ink, rules) re-derives
  // from it. The art-based accents (--sky-accent from the top band, the
  // horizon pop) stay tied to the frame.
  const pageHsl = pageOverride ? rgbToHsl(hexToRgb(pageOverride)) : null;

  let vars;
  let raw;

  if (day) {
    // Sky-colored page, dark ink. The sun curve sets the lightness —
    // that's the whole arc: morning and late-afternoon pages sit lower,
    // noon crests brightest. Hue and saturation come from the frame.
    // Near-gray frames (7am's blue + gold bands cancel each other out)
    // borrow the top band's hue so sunrise reads as a pale morning blue
    // instead of mud.
    let hue = bh;
    let s = Math.min(bs, 0.7);
    if (bs < 0.15) {
      hue = th;
      s = clamp((bs + ts) / 2, 0.2, 0.4);
    }
    let l = sun;
    if (pageHsl) [hue, s, l] = pageHsl;
    const ink = hslToRgb([hue, 0.25, 0.1]);
    const accent = hslToRgb([th, clamp(ts, 0.35, 0.8), 0.3]);
    raw = {
      rule: [hue, s * 0.45, l - 0.18],
      ruleSoft: [hue, s * 0.5, l - 0.1],
      inkMuted: [hue, 0.15, Math.min(0.34, l - 0.32)],
      inkFaint: [hue, 0.12, Math.min(0.46, l - 0.18)],
      inkMutedRaised: [hue, 0.16, Math.min(0.28, l - 0.34)],
      inkFaintRaised: [hue, 0.13, Math.min(0.38, l - 0.24)],
    };
    vars = {
      '--sky-page': hsl(hue, s, l),
      // In-page surfaces (image mats, caption slabs, tiles) stay one quiet
      // step off the page at every tuning: surfaceSep is the chrome's knob,
      // and scaling this with it turned 1pm's mats the chrome's own cyan.
      '--sky-page-edge': hsl(hue, s, l - 0.06),
      '--sky-surface-raised': hsl(hue, s, l - 0.08 * sep),
      '--sky-surface-deep': hsl(hue, s, l - 0.03 * sep),
      '--sky-ink': rgbToHex(ink),
      // The muted/faint steps ride the page lightness down through the
      // dimmer shoulder hours (7am, 6pm) so secondary text keeps its
      // contrast when the page isn't at full brightness.
      '--sky-ink-soft': hsl(hue, 0.18, 0.22),
      '--sky-ink-muted': hsl(...raw.inkMuted),
      '--sky-ink-faint': hsl(...raw.inkFaint),
      '--sky-rule': hsl(...raw.rule),
      '--sky-rule-soft': hsl(...raw.ruleSoft),
      // Deep-sky accent (from the frame's top band) and a horizon accent
      // (from its bottom band) — the sky-mode stand-ins for moss + tan.
      '--sky-accent': rgbToHex(accent),
      '--sky-accent-soft': hsl(th, clamp(ts * 0.6, 0.25, 0.55), 0.45),
      '--sky-tan': hsl(hh, clamp(hs, 0.3, 0.65), 0.38),
      '--sky-highlight': rgba(accent, 0.16),
      '--sky-shadow': rgba(hslToRgb([hue, 0.3, 0.09]), 0.16),
      '--sky-scrim': rgba(hslToRgb([hue, s, l - 0.04 * sep]), 0.86),
      '--sky-scrim-clear': rgba(hslToRgb([hue, s, l - 0.04 * sep]), 0),
      '--sky-scrim-ink': rgba(ink, 0.9),
      // Muted/faint re-tuned for text sitting on --surface-raised
      // (mirrors the [data-theme] .chrome-bar rules in theme.css).
      '--sky-ink-muted-raised': hsl(...raw.inkMutedRaised),
      '--sky-ink-faint-raised': hsl(...raw.inkFaintRaised),
      '--sky-paper-rule': rgba(ink, 0.07),
      '--sky-paper-dot': rgba(ink, 0.1),
    };
  } else {
    // Deep-sky page, light ink, lightness from the sun curve (a hair
    // above black at 6pm's sunset drop, settling to the flat night
    // core). The accent comes from the horizon band, so pre-dawn glows
    // amber, dusk glows orchid, and dead-of-night (horizon too
    // desaturated to mean anything) falls back to a pale moonlit cast
    // of the sky's own hue.
    let hue = bh;
    let s = Math.min(bs, 0.45);
    let l = sun;
    if (pageHsl) [hue, s, l] = pageHsl;
    const ink = hslToRgb([hue, 0.14, 0.89]);
    const glowHue = hs < 0.1 ? hue : hh;
    const glowSat = hs < 0.1 ? 0.22 : hs;
    const accent = hslToRgb([glowHue, clamp(glowSat, 0.3, 0.6), 0.7]);
    raw = {
      rule: [hue, Math.min(s + 0.05, 0.35), l + 0.12],
      ruleSoft: [hue, Math.min(s + 0.03, 0.3), l + 0.06],
      inkMuted: [hue, 0.09, Math.max(0.61, l + 0.26)],
      inkFaint: [hue, 0.08, Math.max(0.45, l + 0.13)],
      inkMutedRaised: [hue, 0.09, Math.max(0.61, l + 0.26)],
      inkFaintRaised: [hue, 0.08, Math.max(0.52, l + 0.18)],
    };
    vars = {
      '--sky-page': hsl(hue, s, l),
      '--sky-page-edge': hsl(hue, s, Math.max(l - 0.02, 0.05)),
      '--sky-surface-raised': hsl(hue, s, Math.max(l - 0.045 * sep, 0.035)),
      '--sky-surface-deep': hsl(hue, s, Math.max(l - 0.075 * sep, 0.02)),
      '--sky-ink': rgbToHex(ink),
      // Muted/faint ride the page lightness up through the brighter
      // shoulder hours (6am dawn, 7pm sunset) so secondary text keeps
      // its contrast against the not-fully-dark page.
      '--sky-ink-soft': hsl(hue, 0.11, 0.78),
      '--sky-ink-muted': hsl(...raw.inkMuted),
      '--sky-ink-faint': hsl(...raw.inkFaint),
      '--sky-rule': hsl(...raw.rule),
      '--sky-rule-soft': hsl(...raw.ruleSoft),
      '--sky-accent': rgbToHex(accent),
      '--sky-accent-soft': hsl(glowHue, clamp(glowSat * 0.8, 0.25, 0.5), 0.58),
      '--sky-tan': hsl(glowHue, clamp(glowSat, 0.25, 0.55), 0.64),
      '--sky-highlight': rgba(accent, 0.16),
      '--sky-shadow': 'rgba(0, 0, 0, 0.45)',
      '--sky-scrim': rgba(hslToRgb([hue, s, Math.max(l - 0.06 * sep, 0.02)]), 0.8),
      '--sky-scrim-clear': rgba(hslToRgb([hue, s, Math.max(l - 0.06 * sep, 0.02)]), 0),
      '--sky-scrim-ink': rgba(ink, 0.9),
      // Night muted already clears the darker raised surface; only faint
      // needs a lift (same shape as the static dark theme's re-tune).
      '--sky-ink-muted-raised': hsl(...raw.inkMutedRaised),
      '--sky-ink-faint-raised': hsl(...raw.inkFaintRaised),
      '--sky-paper-rule': rgba(ink, 0.055),
      '--sky-paper-dot': rgba(ink, 0.08),
    };
  }

  return { vars, ctx: { day, raw } };
}

// The tuning override's warmth/contrast model. Hue + saturation come from
// an RGB blend of the token toward the horizon pop (a natural tan/rose
// path, no magenta detour); lightness is pushed away from the page —
// darker by day, lighter by night — so contrast rises predictably either
// way. warmth 0 && push 0 returns the token unchanged (exact identity).
function warmContrast(baseHsl, popRgb, warmth, push, day) {
  if (warmth <= 0 && push <= 0) return hsl(...baseHsl);
  const targetL = clamp01(baseHsl[2] + (day ? -push : push));
  const b = hslToRgb(baseHsl);
  const mixed = b.map((v, i) => v + (popRgb[i] - v) * warmth);
  const [H, S] = rgbToHsl(mixed);
  return hsl(H, Math.min(S, 0.72), targetL);
}

// The glow / shine box-shadow, split per target group (accent buttons,
// avatar mark, accent text, outlined controls) so each element family can
// opt in independently. Returns 'none' for a group the override doesn't
// target, or when the glow is off.
function glowVarsFrom(cfg) {
  const off = { color: 'transparent', buttons: 'none', avatar: 'none', accent: 'none', controls: 'none' };
  const strength = Number(cfg.glowStrength) || 0;
  const size = Math.round(Number(cfg.glowSize) || 0);
  if (strength <= 0 || size <= 0) return off;
  const rgb = hexToRgb(cfg.glowColor || cfg.pop || '#ffffff');
  const c1 = rgba(rgb, Number(strength.toFixed(3)));
  const c2 = rgba(rgb, Number(Math.min(1, strength * 1.35).toFixed(3)));
  const shadow = `0 0 ${size}px ${Math.round(size * 0.18)}px ${c1}, 0 0 ${Math.round(size * 0.45)}px ${c2}`;
  const text = `0 0 ${Math.round(size * 0.7)}px ${c1}`;
  const T = cfg.glowTargets || {};
  return {
    color: c1,
    buttons: T.buttons ? shadow : 'none',
    avatar: T.avatar ? shadow : 'none',
    accent: T.accentText ? text : 'none',
    controls: T.controls ? shadow : 'none',
  };
}

// The always-present glow vars in their "off" state — so every palette
// carries the full var set (clearSkyTheme/theme.css stay consistent).
const GLOW_OFF = {
  '--sky-glow-color': 'transparent',
  '--sky-glow-buttons': 'none',
  '--sky-glow-avatar': 'none',
  '--sky-glow-accent': 'none',
  '--sky-glow-controls': 'none',
};

/**
 * Layer one hour's parametric tuning override onto its base vars: re-warm
 * the borders and faint/muted ink toward the pop, deepen for contrast,
 * pull the inline highlight from the pop, and emit the per-target glow.
 * Page / primary ink / link accent are left as the base derived them
 * (the page itself may already carry an absolute override via buildBaseVars).
 */
function applyOverride(baseVars, ctx, cfg) {
  const { day, raw } = ctx;
  const popRgb = hexToRgb(cfg.pop || '#ffffff');
  const rW = Number(cfg.ruleWarmth) || 0;
  const rC = Number(cfg.ruleContrast) || 0;
  const iW = Number(cfg.inkWarmth) || 0;
  const iC = Number(cfg.inkContrast) || 0;
  const v = { ...baseVars };
  v['--sky-rule'] = warmContrast(raw.rule, popRgb, rW, rC, day);
  v['--sky-rule-soft'] = warmContrast(raw.ruleSoft, popRgb, rW, rC * 0.55, day);
  v['--sky-ink-faint'] = warmContrast(raw.inkFaint, popRgb, iW, iC, day);
  v['--sky-ink-muted'] = warmContrast(raw.inkMuted, popRgb, iW, iC * 0.6, day);
  v['--sky-ink-faint-raised'] = warmContrast(raw.inkFaintRaised, popRgb, iW, iC, day);
  v['--sky-ink-muted-raised'] = warmContrast(raw.inkMutedRaised, popRgb, iW, iC * 0.6, day);
  if (iW > 0 || rW > 0) {
    const [ph, ps] = rgbToHsl(popRgb);
    v['--sky-tan'] = hsl(ph, clamp(ps, 0.35, 0.72), day ? 0.42 : 0.6);
    v['--sky-accent-soft'] = hsl(ph, clamp(ps * 0.7, 0.3, 0.6), day ? 0.5 : 0.58);
  }
  const g = glowVarsFrom(cfg);
  v['--sky-glow-color'] = g.color;
  v['--sky-glow-buttons'] = g.buttons;
  v['--sky-glow-avatar'] = g.avatar;
  v['--sky-glow-accent'] = g.accent;
  v['--sky-glow-controls'] = g.controls;
  return v;
}

/* ---------- the contrast floor ---------- */

// contrastRatio (WCAG 2) is defined with the colour helpers above.

// sRGB ⇄ OKLab, for the one surface the floor has to reproduce exactly:
// theme.css's nesting scale, which CSS mixes `in oklab`.
const toLinear = (c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const fromLinear = (c) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

function rgbToOklab(rgb) {
  const [r, g, b] = rgb.map((v) => toLinear(v / 255));
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

function oklabToRgb([L, A, B]) {
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ].map((c) => clamp(fromLinear(c), 0, 1) * 255);
}

/** `color-mix(in oklab, a pct%, b)` for two #rrggbb colours. */
export function mixOklab(a, b, pct) {
  const la = rgbToOklab(hexToRgb(a));
  const lb = rgbToOklab(hexToRgb(b));
  return rgbToHex(oklabToRgb(la.map((v, i) => v * pct + lb[i] * (1 - pct))));
}

/** `color-mix(in srgb, a pct%, b)` for two #rrggbb colours. */
export function mixSrgb(a, b, pct) {
  const ra = hexToRgb(a);
  const rb = hexToRgb(b);
  return rgbToHex(ra.map((v, i) => v * pct + rb[i] * (1 - pct)));
}

// CIE76 ΔE between two colours, through OKLab scaled to Lab's range. Only used
// as a "these two inks must not read as one" check, so the cheap metric does.
function deltaE(a, b) {
  const la = rgbToOklab(hexToRgb(a));
  const lb = rgbToOklab(hexToRgb(b));
  return Math.hypot(...la.map((v, i) => (v - lb[i]) * 100));
}

// The floor every text ink is solved to (WCAG AA for body text).
export const SKY_TEXT_FLOOR = 4.5;
// Muted must sit this far above faint, so a floor can't invert the ramp: at
// 7pm a plain floor would leave faint at 4.58 and muted at 4.53.
const RAMP_STEP = 1.15;
// How far apart faint and accent must stay (ΔE, OKLab × 100). At 7am the
// hero's "dame is" lead and its accent clause measure 2.9 apart, close enough
// that the three-tone sentence reads as two. 5 parts them while moving only
// the handful of daytime accents that share faint's hue, and those by less
// than one ratio point.
const FAINT_ACCENT_MIN_DE = 5;
// theme.css: --nest-step. Quote and link cards sit one or two steps off the
// page; the nest inks are solved on the deeper of the two.
export const NEST_STEP = 0.045;
const NEST_SOLVE_LEVEL = 2;

/**
 * Step `hex`'s HSL lightness away from every ground in `grounds` (keeping hue
 * and saturation) until it clears `target` against all of them. `darker`
 * says which way "away" is: ink sits darker than its page by day, lighter by
 * night. Never moves a colour that already clears, and never past `ink`'s own
 * contrast: on a ground too dark for any dark ink (the untuned 7am chrome) a
 * quiet tone stops level with the primary ink instead of going black.
 */
function floorInk(hex, grounds, target, darker, ink) {
  const worst = (c) => Math.min(...grounds.map((g) => contrastRatio(c, g)));
  if (ink) target = Math.min(target, worst(ink));
  if (worst(hex) >= target) return hex;
  const [h, s, l0] = rgbToHsl(hexToRgb(hex));
  let out = hex;
  for (let l = l0; l >= 0 && l <= 1; l += darker ? -0.005 : 0.005) {
    out = hsl(h, s, l);
    if (worst(out) >= target) return out;
  }
  return out;
}

/**
 * The contrast floor: re-solve one hour's text inks against the grounds they
 * actually sit on, after the sky is derived and any tuning is layered on.
 *
 * The derivation only ever stepped lightness by fixed offsets from the page,
 * so whole stretches of the day (dawn, dusk, the pale noon pages) left the
 * quiet inks under 4.5:1. This pass leaves every hue the frame or the owner
 * chose and solves lightness alone, so a floored 6am faint still reads as a
 * grey-rose and 7pm as a lilac — just legible ones.
 *
 * - faint and accent clear the floor on --page;
 * - muted clears max(floor, 1.15 × faint) so the ramp keeps its order, and
 *   soft never drops below muted;
 * - the raised pair clears both chrome surfaces;
 * - a nest pair is solved on the card surface quote and link embeds sit on;
 * - accent keeps a minimum ΔE from faint.
 *
 * Mutates and returns `vars`.
 */
export function solveContrast(vars) {
  const page = vars['--sky-page'];
  const ink = vars['--sky-ink'];
  const darker = luminance(ink) < luminance(page);
  const raised = [vars['--sky-surface-raised'], vars['--sky-surface-deep']];
  const nest = mixOklab(ink, page, NEST_SOLVE_LEVEL * NEST_STEP);
  const F = SKY_TEXT_FLOOR;
  const ratioOn = (c, grounds) => Math.min(...grounds.map((g) => contrastRatio(c, g)));

  const faint = floorInk(vars['--sky-ink-faint'], [page], F, darker, ink);
  vars['--sky-ink-faint'] = faint;
  const muted = floorInk(vars['--sky-ink-muted'], [page], Math.max(F, RAMP_STEP * ratioOn(faint, [page])), darker, ink);
  vars['--sky-ink-muted'] = muted;
  vars['--sky-ink-soft'] = floorInk(vars['--sky-ink-soft'], [page], ratioOn(muted, [page]), darker, ink);

  let accent = floorInk(vars['--sky-accent'], [page], F, darker, ink);
  // Push accent further out (never back toward the page) until it parts
  // from faint. Lightness only, like the floor.
  if (deltaE(accent, faint) < FAINT_ACCENT_MIN_DE) {
    const [h, s, l0] = rgbToHsl(hexToRgb(accent));
    for (let l = l0; l >= 0 && l <= 1; l += darker ? -0.005 : 0.005) {
      accent = hsl(h, s, l);
      if (deltaE(accent, faint) >= FAINT_ACCENT_MIN_DE) break;
    }
  }
  vars['--sky-accent'] = accent;

  const faintR = floorInk(vars['--sky-ink-faint-raised'], raised, F, darker, ink);
  vars['--sky-ink-faint-raised'] = faintR;
  vars['--sky-ink-muted-raised'] = floorInk(
    vars['--sky-ink-muted-raised'],
    raised,
    Math.max(F, RAMP_STEP * ratioOn(faintR, raised)),
    darker,
  );

  const faintN = floorInk(faint, [nest], F, darker, ink);
  vars['--sky-ink-faint-nest'] = faintN;
  vars['--sky-ink-muted-nest'] = floorInk(muted, [nest], Math.max(F, RAMP_STEP * ratioOn(faintN, [nest])), darker, ink);
  vars['--sky-accent-nest'] = floorInk(accent, [nest], F, darker, ink);
  return vars;
}

// Module-level active tuning, installed once the is.dame.sky override
// resolves (snapshot then live — see useSkyTuning). null = the built-in
// hourly derivation, unchanged. Shaped { enabled, hours: {0..23: cfg} }.
let activeTuning = null;

/** Install the resolved sky-theme tuning (or null to clear). */
export function setSkyTuning(tuning) {
  activeTuning = tuning && tuning.enabled ? tuning : null;
}

/** The currently installed tuning (or null). */
export function getSkyTuning() {
  return activeTuning;
}

/**
 * The full token set for one hour of sky, as a map of --sky-* custom
 * properties plus the meta bits (theme-color, color-scheme, label).
 * `tuning` defaults to the installed override; pass an explicit one (e.g.
 * a draft from the admin studio) to preview it without installing it.
 */
export function paletteForHour(hour, tuning = activeTuning) {
  const h24 = ((hour % 24) + 24) % 24;
  const cfg = tuning && tuning.enabled && tuning.hours ? tuning.hours[h24] : null;
  const { vars, ctx } = buildBaseVars(hour, {
    pageOverride: cfg && cfg.page ? cfg.page : null,
    surfaceSep: cfg ? cfg.surfaceSep : 1,
  });
  const tuned = cfg ? applyOverride(vars, ctx, cfg) : { ...vars, ...GLOW_OFF };
  // Two floors. solveContrast sets the page inks to the values the design
  // audit solved for, adds the quote/link-card ("nest") inks and keeps the
  // accent apart from faint ink; withContrastFloor then holds the inks on the
  // sheets and the chrome too, keeps the ramp in order (which can lift the
  // primary ink on an untuned dawn or dusk) and adds --sky-danger. The nest
  // surface is mixed from the primary ink, so solveContrast runs once more to
  // re-solve the card inks against wherever the ink ended up. Every step only
  // lifts a colour that falls short, so later passes leave settled ones be.
  const outVars = solveContrast(withContrastFloor(solveContrast(tuned), ctx.day));
  return {
    hour: h24,
    key: KEYS[h24],
    day: ctx.day,
    colorScheme: ctx.day ? 'light' : 'dark',
    themeColor: outVars['--sky-surface-raised'],
    vars: outVars,
  };
}

/* ---------- contrast floor ---------- */

// WCAG AA for body-size text. The secondary inks carry real reading text —
// timestamps, verb labels, breadcrumbs, the ledger's 10–12px meta — so they
// are held to it like the static themes in theme.css always were.
export const TEXT_CONTRAST_MIN = 4.5;

// The destructive colour's fixed hue, and how much of it survives the mix
// with the hour's ink (theme.css's --danger says why it never takes the sky's
// hue).
const DANGER_RED = '#d4483a';
const DANGER_WEIGHT = 0.74;

// How much stronger muted ink stays than faint ink once the floor has lifted
// faint. Without it, the hours where both had to be lifted would land them
// on the same ratio and flatten the ramp into one grey.
const MUTED_OVER_FAINT = 1.2;

const worstContrast = (hex, backgrounds) =>
  Math.min(...backgrounds.map((bg) => contrastRatio(hex, bg)));

// Move a color's lightness away from its backgrounds — darker on a day
// page, lighter on a night one — just far enough to clear `min` against all
// of them. Hue and saturation are kept, so the ink still reads as the
// hour's own tint; luminance is monotonic in lightness at a fixed hue and
// saturation, which is what makes the bisection valid. A color already
// clear of the floor is returned untouched.
function liftToContrast(hex, backgrounds, min, day) {
  if (worstContrast(hex, backgrounds) >= min) return hex;
  const [h, s, l] = rgbToHsl(hexToRgb(hex));
  let near = l;
  let far = day ? 0 : 1;
  if (worstContrast(hsl(h, s, far), backgrounds) < min) return hsl(h, s, far);
  for (let i = 0; i < 24; i += 1) {
    const mid = (near + far) / 2;
    if (worstContrast(hsl(h, s, mid), backgrounds) >= min) far = mid;
    else near = mid;
  }
  return hsl(h, s, far);
}

/**
 * Hold every text ink in a finished palette to TEXT_CONTRAST_MIN. The
 * derivation sets the secondary inks as fixed lightness offsets from the
 * page, which is right for most of the night and wrong for the shoulder
 * hours — dawn and dusk pages are mid-tones, where an offset that read fine
 * at midnight lands at 2.5:1 — and a tuning override can move them further
 * either way. So the floor runs last, after the override, which also means
 * the admin studio previews exactly what the site will paint.
 *
 * On-page inks are measured against the page AND --surface-deep, the
 * ground of the sheets that render page-level text; the -raised pair
 * against --surface-raised, where the chrome and panels draw them.
 *
 * --danger is floored here too, keeping its own fixed hue (theme.css). It
 * was a plain CSS mix of that red with the hour's ink, which assumed the ink
 * sat far from the page; on the mid-tone dawn and dusk pages it does not, and
 * at 6pm the mix read 2.6:1. It is measured against all three grounds because
 * it is drawn on all three: the page, the sheets' errors, the edit bar.
 */
function withContrastFloor(vars, day) {
  const v = { ...vars };
  const onPage = [v['--sky-page'], v['--sky-surface-deep']];
  const onRaised = [v['--sky-surface-raised']];
  const min = TEXT_CONTRAST_MIN;
  v['--sky-ink-soft'] = liftToContrast(v['--sky-ink-soft'], onPage, min, day);
  v['--sky-ink-faint'] = liftToContrast(v['--sky-ink-faint'], onPage, min, day);
  v['--sky-ink-muted'] = liftToContrast(
    v['--sky-ink-muted'],
    onPage,
    Math.max(
      min,
      worstContrast(v['--sky-ink-faint'], onPage) * MUTED_OVER_FAINT,
    ),
    day,
  );
  // Lifting muted can carry it past soft, and soft past the primary ink (the
  // untuned dawn and dusk pages, where the primary ink itself sits near 5:1).
  // Hold each at least level with the one below, so the ramp
  // (ink >= soft >= muted > faint) never inverts.
  v['--sky-ink-soft'] = liftToContrast(
    v['--sky-ink-soft'],
    onPage,
    worstContrast(v['--sky-ink-muted'], onPage),
    day,
  );
  v['--sky-ink'] = liftToContrast(
    v['--sky-ink'],
    onPage,
    worstContrast(v['--sky-ink-soft'], onPage),
    day,
  );
  v['--sky-ink-faint-raised'] = liftToContrast(
    v['--sky-ink-faint-raised'],
    onRaised,
    min,
    day,
  );
  v['--sky-ink-muted-raised'] = liftToContrast(
    v['--sky-ink-muted-raised'],
    onRaised,
    Math.max(
      min,
      worstContrast(v['--sky-ink-faint-raised'], onRaised) * MUTED_OVER_FAINT,
    ),
    day,
  );
  // The accent is link text as often as it is a fill.
  v['--sky-accent'] = liftToContrast(
    v['--sky-accent'],
    [v['--sky-page']],
    min,
    day,
  );
  v['--sky-danger'] = liftToContrast(
    mixHex(DANGER_RED, v['--sky-ink'], DANGER_WEIGHT),
    [...onPage, ...onRaised],
    min,
    day,
  );
  return v;
}

// Every var name a palette emits, so applySkyTheme writes them all and
// clearSkyTheme can sweep them all: the base tokens, the glow group, and what
// the contrast floor adds.
const SKY_VAR_NAMES = Object.keys(paletteForHour(0, null).vars);

/** Write the given hour's palette onto <html> as --sky-* vars. */
export function applySkyTheme(hour, tuning = activeTuning) {
  const palette = paletteForHour(hour, tuning);
  const root = document.documentElement;
  for (const name of SKY_VAR_NAMES) root.style.setProperty(name, palette.vars[name]);
  // Inline so form controls / scrollbars flip with the hour; theme.css's
  // static `color-scheme: dark` inside [data-theme='sky'] is the fallback.
  root.style.colorScheme = palette.colorScheme;
  return palette;
}

/** Remove every inline --sky-* var (leaving the static themes untouched). */
export function clearSkyTheme() {
  const root = document.documentElement;
  for (const name of SKY_VAR_NAMES) root.style.removeProperty(name);
  root.style.removeProperty('color-scheme');
}
